import { cn } from "@/lib/utils";
import { ShieldCheck, ShieldAlert, Skull } from "lucide-react";
import type { MarginWarningLevel } from "@/backend/types";
import { useMemo } from "react";

// Maintenance margin rates (tiered by leverage) - matches backend defaults
const MAINTENANCE_MARGIN_RATES = {
  tier1: 0.01,    // 1.0% for leverage < 20x
  tier2: 0.0075,  // 0.75% for leverage 20-50x
  tier3: 0.004,   // 0.4% for leverage > 50x
};

// Leverage tier thresholds
const LEVERAGE_TIERS = {
  tier1Max: 20,
  tier2Max: 50,
};

// Get maintenance margin rate based on leverage
function getMaintenanceMarginRate(leverage: number): number {
  if (leverage < LEVERAGE_TIERS.tier1Max) {
    return MAINTENANCE_MARGIN_RATES.tier1;
  } else if (leverage < LEVERAGE_TIERS.tier2Max) {
    return MAINTENANCE_MARGIN_RATES.tier2;
  }
  return MAINTENANCE_MARGIN_RATES.tier3;
}

// Position type for health calculation
type PositionForHealth = {
  size: number;
  entryPrice: number;
  leverage: number;
  margin: number;
};

interface MarginHealthBarProps {
  /** Account equity (collateral + unrealized PnL) */
  equity: number;
  /** Used margin (total margin locked in positions) */
  usedMargin: number;
  /** Open positions for calculating maintenance margin */
  positions?: PositionForHealth[];
  /** Account margin mode */
  accountMode?: "isolated" | "cross";
  /** Override warning level */
  warningLevel?: MarginWarningLevel;
  /** Show label above bar */
  showLabel?: boolean;
  /** Compact mode for smaller displays */
  compact?: boolean;
}

// Warning level thresholds (0-1 scale where 1 = healthy)
// < 0.10 = Danger (90%+ at risk)
// < 0.30 = Warning (70-90% at risk)
// >= 0.30 = Safe (healthy)
type SimplifiedWarningLevel = "safe" | "warning" | "danger";

function getWarningLevelFromHealth(health: number): SimplifiedWarningLevel {
  if (!isFinite(health) || health < 0.10) return "danger";
  if (health < 0.30) return "warning";
  return "safe";
}

const warningConfig = {
  safe: {
    color: "bg-bid-foreground",
    bgColor: "bg-bid/20",
    textColor: "text-bid-foreground",
    icon: ShieldCheck,
    label: "Healthy",
  },
  warning: {
    color: "bg-orange-500",
    bgColor: "bg-orange-500/20",
    textColor: "text-orange-500",
    icon: ShieldAlert,
    label: "Warning",
  },
  danger: {
    color: "bg-ask-foreground",
    bgColor: "bg-ask/20",
    textColor: "text-ask-foreground",
    icon: Skull,
    label: "Danger",
  },
};

/**
 * Calculate account health based on mode:
 * - Cross: Health = min(1 - (maintenanceMargin / equity), 1 - (usedMargin / equity))
 *          Uses the MINIMUM of both metrics for conservative risk assessment
 * - Isolated: Health = clamp(1 - (usedMargin / equity), 0, 1)
 */
function calculateHealth(
  equity: number,
  usedMargin: number,
  positions: PositionForHealth[],
  accountMode: "isolated" | "cross"
): number {
  // No positions = fully healthy
  if (positions.length === 0 || equity <= 0) {
    return 1;
  }

  // Calculate utilization health (used by both modes)
  // This measures: "How much of my equity is locked up in positions?"
  const utilizationHealth = 1 - usedMargin / equity;

  if (accountMode === "cross") {
    // Cross mode: Use the MINIMUM of two metrics for conservative health estimate
    // 1. Maintenance margin approach: theoretical buffer before liquidation
    // 2. Utilization approach: practical risk based on capital deployment
    
    // Calculate total maintenance margin requirement across all positions
    let totalMaintenanceMargin = 0;
    positions.forEach((position) => {
      const notionalValue = Math.abs(position.size) * position.entryPrice;
      const maintenanceRate = getMaintenanceMarginRate(position.leverage);
      totalMaintenanceMargin += notionalValue * maintenanceRate;
    });
    
    // Maintenance health: "What % of equity exceeds minimum liquidation buffer?"
    const maintenanceHealth = 1 - totalMaintenanceMargin / equity;
    
    // Use the minimum (more conservative) of the two metrics
    // This ensures high leverage positions show appropriate risk
    const health = Math.min(maintenanceHealth, utilizationHealth);
    return Math.max(0, Math.min(1, health));
  } else {
    // Isolated mode: Health = 1 - (usedMargin / equity)
    // Measures capital utilization directly
    return Math.max(0, Math.min(1, utilizationHealth));
  }
}

export function MarginHealthBar({
  equity,
  usedMargin,
  positions = [],
  accountMode = "cross",
  warningLevel,
  showLabel = true,
  compact = false,
}: MarginHealthBarProps) {
  // Calculate health metric (0-1 scale)
  const health = useMemo(() => {
    return calculateHealth(equity, usedMargin, positions, accountMode);
  }, [equity, usedMargin, positions, accountMode]);

  // Determine warning level
  const level = warningLevel 
    ? (warningLevel === "caution" ? "warning" : warningLevel as SimplifiedWarningLevel)
    : getWarningLevelFromHealth(health);
  
  const config = warningConfig[level];
  const Icon = config.icon;

  // Fill percentage directly maps health (0-1) to bar fill (0-100%)
  const fillPercent = Math.min(100, Math.max(0, health * 100));

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <div className={cn("w-2 h-2 rounded-full", config.color)} />
        <span className={cn("text-xs font-medium", config.textColor)}>
          {(health * 100).toFixed(0)}%
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {showLabel && (
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground font-medium">Account Health:</span>
            <div className={cn("flex items-center gap-1 px-1.5 py-0.5 rounded", config.bgColor)}>
              <Icon className={cn("size-3", config.textColor)} />
              <span className={cn("font-medium", config.textColor)}>
                {config.label}
              </span>
            </div>
          </div>
          <span className="text-muted-foreground">
            <span className={cn("font-medium", config.textColor)}>
              {(health * 100).toFixed(0)}%
            </span>
          </span>
        </div>
      )}
      
      <div className={cn("relative h-2 rounded-full overflow-hidden", config.bgColor)}>
        <div
          className={cn(
            "absolute inset-y-0 left-0 rounded-full transition-all duration-300",
            config.color
          )}
          style={{ width: `${fillPercent}%` }}
        />
      </div>

      {level === "danger" && (
        <div className="flex items-center gap-1.5 text-xs text-ask-foreground animate-pulse">
          <Skull className="size-3" />
          <span className="font-medium">Account at risk of liquidation!</span>
        </div>
      )}

      {level === "warning" && (
        <div className="flex items-center gap-1.5 text-xs text-orange-500">
          <ShieldAlert className="size-3" />
          <span>Consider reducing leverage or adding margin</span>
        </div>
      )}
    </div>
  );
}

/**
 * Compact health indicator badge
 */
export function MarginHealthIndicator({
  equity,
  usedMargin,
  positions = [],
  accountMode = "cross",
  warningLevel,
}: {
  equity: number;
  usedMargin: number;
  positions?: PositionForHealth[];
  accountMode?: "isolated" | "cross";
  warningLevel?: MarginWarningLevel;
}) {
  const health = useMemo(() => {
    return calculateHealth(equity, usedMargin, positions, accountMode);
  }, [equity, usedMargin, positions, accountMode]);

  const level = warningLevel 
    ? (warningLevel === "caution" ? "warning" : warningLevel as SimplifiedWarningLevel)
    : getWarningLevelFromHealth(health);
  
  const config = warningConfig[level];
  const Icon = config.icon;

  return (
    <div className={cn(
      "flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium",
      config.bgColor,
      config.textColor
    )}>
      <Icon className="size-3.5" />
      <span>{config.label}</span>
    </div>
  );
}

// Export utilities for use in other components
export { calculateHealth, getWarningLevelFromHealth, getMaintenanceMarginRate };
export type { PositionForHealth, SimplifiedWarningLevel };
